/**
 * this component can be accessed from the tax return menu or the client menu
 * the sidebar of either of those pages needs to persist
 */
import {Component, ElementRef, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  SourceAnswerService,
  SourceUploadProcessStateResult,
  SourceService,
  Source,
  ListOfSource,
  TaxReturnTypeService,
  TaxReturnSubTypeService,
  TaxReturnSubType,
  TaxReturnType,
} from "../../../swaggerServices";
import {ITaxReturn} from "../tax-returns/tax-return/model/i-tax-return";
import {Subscription, forkJoin} from "rxjs";
import {TaxReturnsResolver} from "../tax-returns/tax-returns-resolver";
import {ActivatedRoute} from "@angular/router";
import { ConfigStateService } from "../config/config-state.service";
import { UserInfoService } from '../user-info.service';
import { Tenant } from '../../shared/types/tenant.enum';

declare var $: any;

interface ISourceUploadProcessStateResult extends SourceUploadProcessStateResult {
  message?: string; 
  error?: {message?: string};
}

@Component({
    selector: 'app-dynamic-sources-dropdown',
    templateUrl: './dynamic-sources-dropdown.component.html',
    styleUrls: ['./dynamic-sources-dropdown.component.scss'],
    standalone: false
})
export class DynamicSourcesDropdownComponent implements OnInit, OnDestroy {

  bulkAddSourceAnswer?: ISourceUploadProcessStateResult;
  subscriptions: Subscription[] = [];
  sourceId?: number;
  uploadModeAcceptType?: boolean;
  atlasId?: number; // only available when accessed from tax return page
  assigneeId?: number; // only available when accessed from tax return page
  sourceFile?: File;
  isLoading: boolean;
  sourceToAtlasIdMap = new Map();
  sourceToSourceTypeMap = new Map();
  sourceToSubTypeRequiredMap = new Map();
  isAppendOrReplace: string;
  baseSourceList: ListOfSource;
  baseTaxReturnType: TaxReturnType[];
  baseTaxReturnSubtype: TaxReturnSubType[];
  baseTrCombinedTypeList: any[];
  showSubtype = false;
  taxReturnSubType = 0;
  showAppendOrReplace = false;
  selectedTaxReturnSubType = 0;
  tenants: any;
  taxYr: number;

  @ViewChild('dropDown') dropDown: ElementRef;
  @ViewChild('subTypeDropDown') subTypeDropDown: ElementRef;
  @ViewChild('uploadModal') uploadModal: ElementRef;
  @ViewChild('sourceFileUpload') sourceFileUpload: ElementRef;

  constructor(private sourceAnswerService: SourceAnswerService,
              private sourceService: SourceService,
              private trTypeService: TaxReturnTypeService,
              private trSubtypeService: TaxReturnSubTypeService,
              private taxReturnsResolver: TaxReturnsResolver,
              private activatedRoute: ActivatedRoute,
              private userInfo: UserInfoService,
              public config: ConfigStateService) {
  }

  ngOnInit() {
    this.tenants = Tenant;
    this.subscriptions.push(
      this.config.loadConfig().subscribe(config => {
        this.showAppendOrReplace = config.featureFlags.sourceUploadAppendOrReplace;
      })
    );

    this.subscriptions.push(this.taxReturnsResolver.getTaxReturnObs().subscribe((data: ITaxReturn) => {
      this.atlasId = data.atlasId;
      this.assigneeId = data.assigneeId;
      this.taxReturnSubType = data.taxReturnSubTypeId;

      // For ESS please test Replace key and generate id while merging
      this.baseSourceList = this.atlasId ? this.baseSourceList?.filter(s=>(s.requireAtlasId && !s.hidden)) : this.baseSourceList;
      this.initDropdown(this.baseSourceList || []);
    }));

    this.subscriptions.push(this.activatedRoute.queryParams.subscribe(params => {
      if (params.taxYear) this.taxYr = params.taxYear;
    }));

    this.sourceService.getSources().subscribe((d: ListOfSource) => {
      this.baseSourceList = d;
      this.initDropdown(d);
      this.setSourceToAtlasIdAndTypeMap(d);
    });

    forkJoin([
      this.trTypeService.getTaxReturnTypes(),
      this.trSubtypeService.getTaxReturnSubTypes()
    ]).subscribe(([trTypes, trSubtypes]: [TaxReturnType[], any]) => {
      if (trTypes && trSubtypes) {
        this.baseTaxReturnType = trTypes;
        const taxReturnActiveTypeIds = this.baseTaxReturnType.filter(t => t.status === 0).map(t => t.id);
        this.baseTaxReturnSubtype = trSubtypes.subtypes.filter(st => st.status === 0 && taxReturnActiveTypeIds.includes(st.taxReturnTypeId));
        this.initSubtypeDropdown(this.baseTaxReturnSubtype);
      }
    });
  }

  ngOnDestroy() {
    this.subscriptions.forEach(s => s.unsubscribe());
  }

  setSourceToAtlasIdAndTypeMap(sources: ListOfSource) {
    for ( const source of sources) {
      this.sourceToAtlasIdMap.set(source.id, source.requireAtlasId);
      this.sourceToSourceTypeMap.set(source.id, source.type);
      const subTypeRequired = source.requireAtlasId &&
      (
        (!source.config || this.isJsonFormat(source.config) && JSON.parse(source.config).subTypeRequired === undefined) ||
        (source.config && this.isJsonFormat(source.config) && !!JSON.parse(source.config).subTypeRequired)
      )
      this.sourceToSubTypeRequiredMap.set(source.id, subTypeRequired);
    }
  }

  getDefaultSelectedTaxReturnSubType() {
    // We hide the tax return sub type dropdown if there is only one. Select as default if source.subTypeRequired = true
    if (this.baseTaxReturnSubtype.length === 1 &&
      this.sourceId && this.sourceToSubTypeRequiredMap.get(this.sourceId)) {
        return this.baseTaxReturnSubtype[0].id;
      }
    return 0;
  }

  initSubtypeDropdown(subTypes: TaxReturnSubType[]) {
    const valuesPlaceholder: {}[] = [{
      name: 'Select...',
      value: '',
      order: 0
    }];

    const valuesOptions: {}[] = subTypes.map((st: TaxReturnSubType) => ({
      name: st.subType, // the menu label
      value: st.id,
    }));

    // init semantic-ui dropdown
    $(this.subTypeDropDown.nativeElement).dropdown({
      values: [...valuesPlaceholder, ...valuesOptions],
      placeholder: 'Select...',
      fullTextSearch: 'exact',
      preserveHTML: false,
      onChange: (value: string) => {
        const found = this.baseTaxReturnSubtype.find(st => st.id === +value);
        this.selectedTaxReturnSubType = (found) ? found.id : 0;
      }
    });
  }

  initDropdown(sources: ListOfSource) {
    const valuesPlaceholder: {}[] = [{
      name: 'Select...',
      value: '',
      order: 0
    }];

    const isAdmin = this.userInfo.isAdmin;

    const isCorrectType = source => {
      const allowedTypes = ['prefill', 'csv', 'fx-day', 'oneteam'];
      const allowedTypesWithAtlasId = ['pdf'];
      if (allowedTypes.some( type => type === source.type)) return true;
      return this.atlasId && allowedTypesWithAtlasId.some(type => type === source.type);
    }

    const valuesOptions: {}[] = sources.filter((s: Source) => {

      if (s.config && this.isJsonFormat(s.config)) {
        const isAdminOnly = JSON.parse(s.config).adminOnly;
        if (!!isAdminOnly) {
          if (isAdmin) {
            return isCorrectType(s);
          }
        } else {
          return isCorrectType(s);
        }
      } else {
        return isCorrectType(s);
      }
    }).map((s: Source) => ({
      name: s.name, // the menu label
      value: s.id
    }));

    // init semantic-ui dropdown
    $(this.dropDown.nativeElement).dropdown({
      values: valuesPlaceholder.concat(valuesOptions),
      placeholder: 'Select...',
      fullTextSearch: 'exact',
      preserveHTML: false,
      onChange: (value: string) => {
        this.sourceId = Number(value);
        if (this.sourceId !== 0) {
          this.isAppendOrReplace = this.sourceToAtlasIdMap.get(this.sourceId) === true ? 'replace' : 'append';
          const sourceType = this.sourceToSourceTypeMap.get(this.sourceId).toLowerCase();
          if (sourceType === 'pdf') {
            $(this.subTypeDropDown.nativeElement).dropdown('set selected', this.taxReturnSubType);
            $(this.subTypeDropDown.nativeElement).attr("class", "ui search selection dropdown disabled");
          } else {
            this.selectedTaxReturnSubType = 0;
            $(this.subTypeDropDown.nativeElement).dropdown('set selected', '');
            $(this.subTypeDropDown.nativeElement).attr("class", "ui search selection dropdown");
          }
          this.uploadModeAcceptType = sourceType === 'csv' || sourceType === 'fx-day' ? true : false;
          this.selectedTaxReturnSubType = 0;
          this.showSubtype = this.displayTaxReturnSubType();
        } else {
          this.showSubtype = false;
        }
      }
    });
  }

  displayTaxReturnSubType(): boolean {
    const defaultSubType = this.getDefaultSelectedTaxReturnSubType();
    if (defaultSubType) {
      $(this.subTypeDropDown.nativeElement).dropdown('set selected', this.getDefaultSelectedTaxReturnSubType());
    }
    return this.baseSourceList && this.baseSourceList.length > 1 &&
      this.baseTaxReturnSubtype && this.baseTaxReturnSubtype.length > 1 &&
      this.sourceId && this.sourceToSubTypeRequiredMap.get(this.sourceId);
  }

  isJsonFormat(str: string): boolean {
    try {
      const a = JSON.parse(str);
      return (a);
    } catch (e) {
      return false;
    }
  }

  updateUploadMode(mode: string) {
    this.isAppendOrReplace = mode;
  }

  // validates for csv file format
  initFile(files: any) {
    this.sourceFile = undefined;
    this.bulkAddSourceAnswer = {};

    const pattern = (/\.(csv|xlsx|xml|pdf|zip)$/i);
    const isValidType = pattern.test(files[0].name);

    if (!isValidType) {
      this.bulkAddSourceAnswer = {
        error: {
          message: this.atlasId ? "Please upload a file in .csv, .xlsx, .pdf, or .zip format." : "Please upload a file in .csv, .xlsx or .zip format"
        }
      };
      $(this.uploadModal.nativeElement).modal('show');
    } else {
      this.sourceFile = files[0];
    }
  }

  // bulk uploads list of dynamic sources
  bulkUpload() {
    this.isLoading = true;
    const taxYr = this.activatedRoute.queryParams['value'].taxYear;
    const clientId = this.activatedRoute.queryParams['value'].clientId;
    this.sourceAnswerService.bulkAddSourceAnswer(this.sourceFile, taxYr, this.sourceId, this.atlasId, this.assigneeId, clientId, this.selectedTaxReturnSubType, this.isAppendOrReplace)
      .subscribe(
        (d: SourceUploadProcessStateResult) => {
          this.isLoading = false;
          this.bulkAddSourceAnswer = d;
          $(this.uploadModal.nativeElement).modal('show');
          this.sourceFileUpload.nativeElement.value = '';
          this.sourceFile = undefined;
        },
        (err) => {
          this.isLoading = false;
          this.bulkAddSourceAnswer = {
            error: {
              message: err.error.message
            }
          };
          $(this.uploadModal.nativeElement).modal('show');
          this.sourceFileUpload.nativeElement.value = '';
          this.sourceFile = undefined;
        }
      );
  }

  isUploadEnabled() {
    return (this.sourceId && (!this.showSubtype || this.selectedTaxReturnSubType));
  }
}
